/* Auto-generated base adapted for TypeScript */
import * as THREE from 'three'
import { useEffect, useRef } from 'react'
import { useGLTF, CubeCamera } from '@react-three/drei'
import { Group } from 'three'
import React from 'react'

type CarMaterialControls = {
  paintColor: string
  paintMetalness: number
  paintRoughness: number
  paintClearcoat: number
  paintEnvMapIntensity: number
  carEnvIntensity: number
  paintAoMapIntensity: number
  paintRoughnessMapIntensity: number
  chromeMetalness: number
  chromeRoughness: number
  chromeColor: string
  glassColor: string
  glassOpacity: number
  plasticsRoughness: number
  lightsEmissiveIntensity: number
  tireColor: string
  tireRoughness: number
}

type CarProps = JSX.IntrinsicElements['group'] & Partial<CarMaterialControls>

export default function Car({
  paintColor = '#ffdf71',
  paintMetalness = 0.2,
  paintRoughness = 0,
  paintClearcoat = 1,
  paintEnvMapIntensity = 1.5,
  carEnvIntensity = 1.0,
  paintAoMapIntensity = 1.5,
  paintRoughnessMapIntensity = 2,
  chromeMetalness = 1,
  chromeRoughness = 0.3,
  chromeColor = '#ffffff',
  glassColor = '#ffffff',
  glassOpacity = 0.6,
  plasticsRoughness = 0.8,
  lightsEmissiveIntensity = 50,
  tireColor = '#000000',
  tireRoughness = 0.7,
  ...props
}: CarProps) {
  const group = useRef<Group>(null)
  // GLB is placed under public/
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { nodes, materials } = useGLTF('/porsche-transformed.glb') as unknown as { nodes: Record<string, any>, materials: Record<string, any> }

  // Apply UI state to materials when available
  useEffect(() => {
    if (!materials) return
    if (materials.paint) {
      materials.paint.color.set(paintColor)
      materials.paint.metalness = paintMetalness
      materials.paint.roughness = paintRoughness
      // clearcoat is supported by MeshPhysicalMaterial (glTF likely uses it)
      materials.paint.clearcoat = paintClearcoat
      // Combine local paint control with global car environment intensity
      materials.paint.envMapIntensity = paintEnvMapIntensity * carEnvIntensity
      materials.paint.aoMapIntensity = paintAoMapIntensity
      materials.paint.roughnessMapIntensity = paintRoughnessMapIntensity
      materials.paint.needsUpdate = true
    }
    if (materials['930_chromes']) {
      materials['930_chromes'].metalness = chromeMetalness
      materials['930_chromes'].roughness = chromeRoughness
      materials['930_chromes'].color = new THREE.Color(chromeColor)
      // Boost reflections from environment
      materials['930_chromes'].envMapIntensity = carEnvIntensity
      materials['930_chromes'].needsUpdate = true
    }
    if (materials['glass']) {
      materials['glass'].color = new THREE.Color(glassColor)
      materials['glass'].opacity = glassOpacity
      materials['glass'].transparent = true
      materials['glass'].depthWrite = false
      // Some glass materials support envMapIntensity as well
      materials['glass'].envMapIntensity = carEnvIntensity
      materials['glass'].needsUpdate = true
    }
    if (materials['930_plastics']) {
      materials['930_plastics'].roughness = plasticsRoughness
      materials['930_plastics'].envMapIntensity = carEnvIntensity
      materials['930_plastics'].needsUpdate = true
    }
    if (materials['930_lights']) {
      // Keep emissiveMap bound to its albedo map
      materials['930_lights'].emissiveMap = materials['930_lights'].map
      materials['930_lights'].emissiveIntensity = lightsEmissiveIntensity
      materials['930_lights'].needsUpdate = true
    }
    if (materials['930_tire']) {
      materials['930_tire'].color.set(tireColor)
      materials['930_tire'].roughness = tireRoughness
      materials['930_tire'].envMapIntensity = carEnvIntensity
      materials['930_tire'].needsUpdate = true
    }
    if (materials['930_rim']) {
      materials['930_rim'].envMapIntensity = carEnvIntensity
      materials['930_rim'].needsUpdate = true
    }
    if (materials['black']) {
      materials['black'].envMapIntensity = carEnvIntensity
      materials['black'].needsUpdate = true
    }
  }, [
    materials,
    paintColor,
    paintMetalness,
    paintRoughness,
    paintClearcoat,
    paintEnvMapIntensity,
    carEnvIntensity,
    paintAoMapIntensity,
    paintRoughnessMapIntensity,
    chromeMetalness,
    chromeRoughness,
    chromeColor,
    glassColor,
    glassOpacity,
    plasticsRoughness,
    lightsEmissiveIntensity,
    tireColor,
    tireRoughness,
  ])

  return (
    <group ref={group} {...props} dispose={null}>
      <CubeCamera frames={1} position={[0, 1.5, 0]} near={0.1} resolution={128}>
        {(texture) => (
          <group position={[0, -1.5, 0]}>
            <group rotation={[Math.PI / 2, 0, 0]}>
              <mesh
                geometry={nodes.mesh_1_instance_0.geometry}
                material={materials['930_plastics']}
                position={[-7.966238, -0.10155, -7.966238]}
                scale={0.000973}
              />
              <mesh
                geometry={nodes.mesh_1_instance_1.geometry}
                material={materials['930_plastics']}
                position={[-7.966238, -0.10155, -7.966238]}
                scale={0.000973}
              />
            </group>
            <group position={[-7.966238, -0.10155, -7.966238]} scale={0.000973}>
              <mesh geometry={nodes.mesh_0.geometry} material={materials.paint} material-envMap={texture} />
              <mesh geometry={nodes.mesh_0_1.geometry} material={materials['930_chromes']} />
              <mesh geometry={nodes.mesh_0_2.geometry} material={materials.black} />
              <mesh geometry={nodes.mesh_0_3.geometry} material={materials['930_lights']} />
              <mesh geometry={nodes.mesh_0_4.geometry} material={materials.glass} />
              <mesh geometry={nodes.mesh_0_5.geometry} material={materials['930_stickers']} />
              <mesh geometry={nodes.mesh_0_6.geometry} material={materials['930_plastics']} material-polygonOffset material-polygonOffsetFactor={-10} />
              <mesh geometry={nodes.mesh_0_7.geometry} material={materials['930_lights_refraction']} />
              <mesh geometry={nodes.mesh_0_8.geometry} material={materials['930_rim']} />
              <mesh geometry={nodes.mesh_0_9.geometry} material={materials['930_tire']} />
            </group>
          </group>
        )}
      </CubeCamera>
      {/* <group position={[-7.966238, -0.10155, -7.966238]} scale={0.000973}>
        <mesh geometry={nodes.mesh_2.geometry} material={materials.plate} material-roughness={1} />
        <mesh geometry={nodes.mesh_2_1.geometry} material={materials.DefaultMaterial} />
        <mesh geometry={nodes.mesh_2_2.geometry} material={materials['Material.001']} material-depthWrite={false} material-opacity={0.6} />
      </group> */}
    </group>
  )
}

useGLTF.preload('/porsche-transformed.glb')


